import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/Pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pencil, Trash2, KeyRound, RotateCcw, Search, Eye, EyeOff, Loader2, AlertTriangle, History } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import api from '@/lib/api';

const PAGE_SIZE = 15;

const roleBadge = {
  citizen: { label: 'role.citizen', className: 'bg-blue-100 text-blue-800' },
  embassy: { label: 'role.embassy', className: 'bg-green-100 text-green-800' },
  admin: { label: 'role.admin', className: 'bg-purple-100 text-purple-800' },
};

export default function ManageUsers() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [embassies, setEmbassies] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(1);

  // Edit dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Password dialog
  const [pwdDialog, setPwdDialog] = useState(false);
  const [pwdUser, setPwdUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Reset dialog
  const [resetDialog, setResetDialog] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetting, setResetting] = useState(false);

  // Activity dialog
  const [activityDialog, setActivityDialog] = useState(false);
  const [activityUser, setActivityUser] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  const load = () => api.get('/users').then(r => setUsers(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get('/embassies').then(r => setEmbassies(r.data)).catch(() => {});
  }, []);

  // Filtered users
  const filtered = users.filter(u => {
    const matchSearch = !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    return matchSearch && matchRole;
  });
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // === EDIT USER ===
  const openEdit = (user) => {
    setEditUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      date_of_birth: user.date_of_birth || '',
      country_of_residence: user.country_of_residence || '',
      nationality: user.nationality || '',
      profession: user.profession || '',
      role: user.role || 'citizen',
      embassy_id: user.embassy_id || '',
    });
    setEditDialog(true);
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${editUser.user_id}`, editForm);
      toast.success('Utilisateur modifie avec succes');
      setEditDialog(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  // === SET PASSWORD ===
  const openPassword = (user) => {
    setPwdUser(user);
    setNewPassword('');
    setShowPassword(false);
    setPwdDialog(true);
  };

  const handleSetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    setPwdSaving(true);
    try {
      await api.put(`/users/${pwdUser.user_id}/password`, { password: newPassword });
      toast.success('Mot de passe modifie avec succes');
      setPwdDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setPwdSaving(false);
    }
  };

  // === RESET PASSWORD ===
  const openReset = (user) => {
    setResetUser(user);
    setResetDialog(true);
  };

  const handleResetPassword = async () => {
    setResetting(true);
    try {
      const res = await api.post(`/users/${resetUser.user_id}/reset-password`);
      toast.success(res.data.message);
      setResetDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setResetting(false);
    }
  };

  // === DELETE USER ===
  const openDelete = (user) => {
    setDeleteUser(user);
    setDeleteDialog(true);
  };

  // === ACTIVITY HISTORY ===
  const openActivity = async (user) => {
    setActivityUser(user);
    setActivities([]);
    setActivityDialog(true);
    setLoadingActivity(true);
    try {
      const res = await api.get(`/users/${user.user_id}/activity`);
      setActivities(res.data);
    } catch {
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoadingActivity(false);
    }
  };

  const actionLabels = {
    login: { label: 'Connexion', color: 'bg-blue-100 text-blue-800' },
    profile_updated: { label: 'Profil modifie', color: 'bg-indigo-100 text-indigo-800' },
    application_created: { label: 'Dossier cree', color: 'bg-green-100 text-green-800' },
    application_submitted: { label: 'Dossier soumis', color: 'bg-sky-100 text-sky-800' },
    application_resubmitted: { label: 'Dossier resoumis', color: 'bg-sky-100 text-sky-800' },
    application_approved: { label: 'Dossier approuve', color: 'bg-emerald-100 text-emerald-800' },
    application_rejected: { label: 'Dossier rejete', color: 'bg-red-100 text-red-800' },
    document_uploaded: { label: 'Document uploade', color: 'bg-yellow-100 text-yellow-800' },
    card_viewed: { label: 'Carte consultee', color: 'bg-purple-100 text-purple-800' },
    appointment_created: { label: 'RDV cree', color: 'bg-teal-100 text-teal-800' },
    appointment_cancelled: { label: 'RDV annule', color: 'bg-orange-100 text-orange-800' },
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteUser.user_id}`);
      toast.success('Utilisateur supprime avec succes');
      setDeleteDialog(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setDeleting(false);
    }
  };

  const updateForm = (field, value) => setEditForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="manage-users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('admin.users')}</h1>
        <Badge variant="outline" className="text-sm">{filtered.length} utilisateur(s)</Badge>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom ou email..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
            data-testid="user-search-input"
          />
        </div>
        <Select value={roleFilter} onValueChange={v => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[160px]" data-testid="user-role-filter">
            <SelectValue placeholder="Tous les roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="embassy">Ambassade</SelectItem>
            <SelectItem value="citizen">Citoyen</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <Card className="border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('app.full_name')}</TableHead>
                  <TableHead>{t('app.email')}</TableHead>
                  <TableHead>{t('admin.role')}</TableHead>
                  <TableHead>{t('app.country')}</TableHead>
                  <TableHead>Inscription</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.map(u => {
                  const rb = roleBadge[u.role] || roleBadge.citizen;
                  return (
                    <TableRow key={u.user_id}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-sm">{u.email}</TableCell>
                      <TableCell><Badge className={rb.className}>{t(rb.label)}</Badge></TableCell>
                      <TableCell className="text-sm">{u.country_of_residence || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-0.5 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => openActivity(u)} title="Historique d'activite" data-testid={`activity-user-${u.user_id}`}>
                            <History className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(u)} title="Modifier l'utilisateur" data-testid={`edit-user-${u.user_id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openPassword(u)} title="Definir le mot de passe" data-testid={`set-pwd-${u.user_id}`}>
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openReset(u)} title="Reinitialiser le mot de passe" data-testid={`reset-pwd-${u.user_id}`}>
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openDelete(u)} title="Supprimer l'utilisateur" data-testid={`delete-user-${u.user_id}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {paginated.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Aucun utilisateur trouve</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="px-4 pb-4">
            <Pagination total={filtered.length} pageSize={PAGE_SIZE} currentPage={page} onPageChange={setPage} />
          </div>
        </CardContent>
      </Card>

      {/* EDIT USER DIALOG */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby="edit-user-desc">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Modifier l'utilisateur</DialogTitle>
            <DialogDescription id="edit-user-desc">Modifiez les informations de {editUser?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Nom complet</Label>
                <Input value={editForm.name || ''} onChange={e => updateForm('name', e.target.value)} data-testid="edit-user-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input value={editForm.email || ''} onChange={e => updateForm('email', e.target.value)} data-testid="edit-user-email" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Telephone</Label>
                <Input value={editForm.phone || ''} onChange={e => updateForm('phone', e.target.value)} data-testid="edit-user-phone" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Date de naissance</Label>
                <Input value={editForm.date_of_birth || ''} onChange={e => updateForm('date_of_birth', e.target.value)} placeholder="JJ/MM/AAAA" data-testid="edit-user-dob" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Pays de residence</Label>
                <Input value={editForm.country_of_residence || ''} onChange={e => updateForm('country_of_residence', e.target.value)} data-testid="edit-user-country" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Nationalite</Label>
                <Input value={editForm.nationality || ''} onChange={e => updateForm('nationality', e.target.value)} data-testid="edit-user-nationality" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Profession</Label>
              <Input value={editForm.profession || ''} onChange={e => updateForm('profession', e.target.value)} data-testid="edit-user-profession" />
            </div>

            <div className="p-3 rounded-lg border bg-muted/30 space-y-3">
              <Label className="font-medium text-sm">Role et affectation</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Role</Label>
                  <Select value={editForm.role} onValueChange={v => updateForm('role', v)}>
                    <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="citizen">Citoyen</SelectItem>
                      <SelectItem value="embassy">Ambassade</SelectItem>
                      <SelectItem value="admin">Administrateur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editForm.role === 'embassy' && (
                  <div className="space-y-1">
                    <Label className="text-xs">Ambassade</Label>
                    <Select value={editForm.embassy_id} onValueChange={v => updateForm('embassy_id', v)}>
                      <SelectTrigger data-testid="edit-user-embassy"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                      <SelectContent>
                        {embassies.map(e => <SelectItem key={e.embassy_id} value={e.embassy_id}>{e.country} - {e.city}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditDialog(false)}>Annuler</Button>
            <Button className="bg-[#007FFF] hover:bg-[#0066CC]" onClick={handleEditSave} disabled={saving || !editForm.name || !editForm.email} data-testid="save-edit-user-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SET PASSWORD DIALOG */}
      <Dialog open={pwdDialog} onOpenChange={setPwdDialog}>
        <DialogContent aria-describedby="set-pwd-desc">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Definir le mot de passe</DialogTitle>
            <DialogDescription id="set-pwd-desc">Definir un nouveau mot de passe pour {pwdUser?.name} ({pwdUser?.email})</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <p>Le mot de passe sera modifie immediatement. L'utilisateur sera notifie du changement.</p>
            </div>
            <div className="space-y-1">
              <Label>Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 caracteres"
                  data-testid="set-password-input"
                />
                <Button
                  variant="ghost" size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdDialog(false)}>Annuler</Button>
            <Button className="bg-[#007FFF] hover:bg-[#0066CC]" onClick={handleSetPassword} disabled={pwdSaving || newPassword.length < 6} data-testid="confirm-set-password-btn">
              {pwdSaving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Modifier le mot de passe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RESET PASSWORD DIALOG */}
      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent aria-describedby="reset-pwd-desc">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Reinitialiser le mot de passe</DialogTitle>
            <DialogDescription id="reset-pwd-desc">Reinitialiser le mot de passe de {resetUser?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
              <p>Un mot de passe temporaire sera genere et envoye par email a <strong>{resetUser?.email}</strong>.</p>
              <p className="mt-1">L'utilisateur devra se connecter avec ce nouveau mot de passe.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialog(false)}>Annuler</Button>
            <Button className="bg-[#007FFF] hover:bg-[#0066CC]" onClick={handleResetPassword} disabled={resetting} data-testid="confirm-reset-password-btn">
              {resetting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Reinitialiser et envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE USER DIALOG */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent aria-describedby="delete-user-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Supprimer l'utilisateur
            </DialogTitle>
            <DialogDescription id="delete-user-desc">Cette action est irreversible</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
              <p>Vous etes sur le point de supprimer definitivement :</p>
              <div className="mt-2 p-2 bg-white rounded border border-red-200">
                <p className="font-medium">{deleteUser?.name}</p>
                <p className="text-xs">{deleteUser?.email}</p>
              </div>
              <p className="mt-2 font-medium">Toutes les donnees associees seront egalement supprimees :</p>
              <ul className="list-disc list-inside mt-1 text-xs space-y-0.5">
                <li>Dossiers et documents</li>
                <li>Cartes emises</li>
                <li>Rendez-vous</li>
                <li>Notifications</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-user-btn">
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Supprimer definitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ACTIVITY HISTORY DIALOG */}
      <Dialog open={activityDialog} onOpenChange={setActivityDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="activity-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <History className="w-5 h-5 text-[#007FFF]" />
              Historique d'activite
            </DialogTitle>
            <DialogDescription id="activity-desc">{activityUser?.name} ({activityUser?.email})</DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            {loadingActivity ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#007FFF]" />
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">Aucune activite enregistree</div>
            ) : (
              <div className="space-y-1">
                {activities.map((a, i) => {
                  const info = actionLabels[a.action] || { label: a.action, color: 'bg-gray-100 text-gray-800' };
                  return (
                    <div key={a.activity_id || i} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors" data-testid={`activity-item-${i}`}>
                      <div className="text-xs text-muted-foreground whitespace-nowrap pt-0.5 w-[110px] shrink-0">
                        {a.created_at ? new Date(a.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </div>
                      <Badge className={`${info.color} text-[10px] shrink-0`}>{info.label}</Badge>
                      <span className="text-sm text-muted-foreground flex-1 truncate">{a.detail}</span>
                      {a.ip && <span className="text-[10px] text-muted-foreground/60 shrink-0">{a.ip}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
